import './tasks-list.scss'
import {reaction} from 'mobx'
import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import {Lang} from 'yougile-lang'
import {UUID} from 'yougile-util'
import {DataObjectId, Hub, TaskListType, TasksListId} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Backend} from '@common/backend'
import {
  DragData,
  DragData as DragDataClass,
  DragDataObject
} from '@common/drag-data'
import {Tracking, TrackingEvent} from '@common/tracking'
import {
  ButtonNew,
  IconButtonNew,
  LinkButtonNew
} from '@components/common/base/buttons'
import {CheckboxBase} from '@components/common/base/checkbox-new/checkbox-base'
import {Dialog} from '@components/common/base/dialog/dialog'
import {Icon} from '@components/common/base/icon'
import {InputNew} from '@components/common/base/input-new'
import {Menu} from '@components/common/base/menu'
import {Tag} from '@components/common/base/tag'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {ListView} from '@components/common/sandbox/list-view/list-view'
import {
  AddMode,
  CapturedByListEvent,
  ListStoreExposedApi,
  NullableId,
  Placement,
  RowConfig,
  RowContent,
  SortSource
} from '@components/common/sandbox/list-view/types'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {MessageBox} from '@components/message-box'
import {PromoSettings} from '@components/my-tasks/tasks-list/components/promo-settings'
import {SortMenuDropdown} from '@components/my-tasks/tasks-list/components/sort-menu-dropdown'
import {TaskRow} from '@components/my-tasks/tasks-list/components/task-row'
import {
  TwAddListBtnWrapper,
  TwAddNewList,
  TwAppliedListTool,
  TwEditListWrapper,
  TwHeaderConfigGroup,
  TwHeaderFunctions,
  TwHeaderLeftArea,
  TwHeaderTitle,
  TwHeaderTitleBlock,
  TwHeaderTitleContainer,
  TwIconButton,
  TwListHeader,
  TwStub,
  TwTableSettingOption,
  TwTableSettingOptionLabel,
  TwTableSettingOptions,
  TwTableSettingsTitle,
  TwTableSettingsWrapper,
  TwTableSettingWrapper,
  TwTaskListInner,
  TwTasksList,
  TwTaskTitleCountBadge,
  TwTitleInnerWrapper,
  TwTitleWrapper
} from '@components/my-tasks/tasks-list/containers'
import {parseTaskToRows} from '@components/my-tasks/tasks-list/helpers/parse-task-to-rows'
import {getTaskTitle} from '@components/my-tasks/tasks-list/helpers/task-title-helper'
import {TaskListFilters} from '@components/my-tasks/tasks-list/task-list-filters'
import {
  TaskListStore,
  TasksListContext,
  useTasksListStore
} from '@components/my-tasks/tasks-list/tasks-list-store'
import {
  TasksListsContext,
  TasksListsStore,
  useTasksListsStore
} from '@components/my-tasks/tasks-list/tasks-lists-store'
import {editListMenuOptions} from '@components/my-tasks/utils'
import {WithResizeObserver} from '@components/sticker/sticker-panel/resize-observer'
import {keyboardHandler, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {TaskMenu} from './components/task-menu'

/**
 * Readonly empty enabled columns for private lists
 */
const EmptyEnabledColumns: string[] = []
export const TasksLists = () => {
  const [store] = useState(() => new TasksListsStore())

  useEffect(() => {
    return () => {
      store.dispose()
    }
  }, [])

  return (
    <TasksListsContext.Provider value={store}>
      <TasksListsInner />
    </TasksListsContext.Provider>
  )
}

/*
const prepareDragData: PrepareDragData = (path, overPath, before) => {
  if (path === overPath) {
    return undefined
  }
  return {
    rowId: overPath,
    position: before ? 'before' : 'after'
  }
}
 */

/**
 * Handle row click. It opens chat with task
 * @param row
 * @param event
 */
const onRowClick = (
  row: RowConfig<Hub>,
  event: React.MouseEvent<HTMLDivElement>
) => {
  event.stopPropagation()
  const task = Task.getById(row.data.id)

  const canShow = PermissionsChecker.checkShow('ignore-departments', task, {})

  if (!task || !canShow) {
    MessageBox.warn(Lang.msg.subtask.noPermissionForTask)
    return
  }

  const openedChat = ChatUtils.getOpenedChatHub()

  if (openedChat?.id === task.id) {
    ChatUtils.closeChatIfOpened()
    return
  }

  ChatUtils.openChat(task)
  void Tracking.send(TrackingEvent.myTasksOpenTaskViaMyTaskSection)
}

/**
 * Special task row renderer provides task store for each row
 * @param content
 * @param row
 */
const rowRenderer = (content: RowContent, row: RowConfig<Hub>) => {
  return <TaskRow taskId={row.data.id}>{content}</TaskRow>
}

/**
 * Container that renders lists
 */
export const TasksListsInner = observer(() => {
  const [state, store] = useTasksListsStore()
  const scrollContainer = useRef<ScrollBar>(null)

  useSyncEvent(
    'profile',
    {filter: globals.getCompanyId(), debounceTime: 100},
    store.syncTasksLists
  )

  const onResizeHeight = () => {
    if (scrollContainer.current) {
      scrollContainer.current.updateScroll()
    }
  }

  const keyDown = keyboardHandler({
    enter: store.submitNewList,
    escape: store.cancelAddNewList
  })

  const toggleAddList = (listType: TaskListType) => {
    store.setAddListVisible(listType)
  }

  const firstNonPrivateListIndex = state.tasksListsOrder.findIndex(
    listId => !state.privateTasksLists.includes(listId)
  )

  const onMount = useCallback(
    (listId: DataObjectId, mountedStore: TaskListStore) => {
      store.registerTaskListStore(listId, mountedStore)
    },
    []
  )

  const onUnmount = useCallback((listId: DataObjectId) => {
    store.unregisterTaskListStore(listId)
  }, [])

  return (
    <WithPerfectScrollbar
      drag
      ref={scrollContainer}
      className="flex-1"
      scrollDirection="xy"
    >
      {Backend.features.myTasksLists && (
        <TwAddListBtnWrapper>
          <ButtonNew
            title={Lang.msg.myTasksAddList}
            onClick={() => toggleAddList('custom')}
          />
          <ButtonNew
            title={Lang.msg.myTasksAddPrivateList}
            onClick={() => toggleAddList('private')}
          />
        </TwAddListBtnWrapper>
      )}
      {!!state.visibleAddListType && (
        <TwAddNewList>
          <InputNew
            inputClassName="!text-20 !h-auto !font-semibold !py-8 !bg-background-hover"
            size="lg"
            leftIconDisabled
            cursorView
            autoFocus
            value={state.newListTitle}
            iconLeft="IconChevronDown"
            onChange={store.setNewListTitle}
            onBlur={() => {
              store.submitNewList()
              store.cancelAddNewList()
            }}
            onKeyDown={keyDown}
            placeholder={
              state.visibleAddListType === 'private'
                ? Lang.msg.myTasksPrivateListName
                : Lang.msg.myTasksListName
            }
          />
        </TwAddNewList>
      )}
      <WithResizeObserver
        callback={onResizeHeight}
        className="w-fit min-w-full"
      >
        <TwTasksList>
          {state.tasksListsOrder.map((listId, index) => {
            return (
              <TaskListWrapper
                key={listId}
                listId={listId}
                showPromoSettingsBlockHint={
                  index === firstNonPrivateListIndex &&
                  !store.promoSettingsIsSeen
                    ? store.setPromoSettingsBlockSeen
                    : undefined
                }
                onMount={onMount}
                onUnmount={onUnmount}
              />
            )
          })}
        </TwTasksList>
      </WithResizeObserver>
    </WithPerfectScrollbar>
  )
})

interface TaskListWrapperProps {
  listId: TasksListId
  showPromoSettingsBlockHint?: () => void
  onMount: (listId: TasksListId, store: TaskListStore) => void
  onUnmount: (listId: TasksListId) => void
}

export const TaskListWrapper = ({
  listId,
  showPromoSettingsBlockHint,
  onMount,
  onUnmount
}: TaskListWrapperProps) => {
  const [, listsStore] = useTasksListsStore()
  const [store] = useState(() => new TaskListStore(listId, listsStore))

  /**
   * Initialize and release task list store on mount and unmount
   * It gives the ability to use task list store in the parent tasks lists store
   */
  useEffect(() => {
    onMount(listId, store)

    return () => {
      onUnmount(listId)
    }
  }, [])

  return (
    <TasksListContext.Provider value={store}>
      <TaskListInner showPromoSettingsBlockHint={showPromoSettingsBlockHint} />
    </TasksListContext.Provider>
  )
}

const inputTitleStyle: Partial<CSSStyleDeclaration> = {
  whiteSpace: 'pre',
  fontWeight: 'normal',
  fontSize: '22px',
  lineHeight: '30px'
}

const isTaskDone = (task: Hub) => !!Task.isDone(task)

/*eslint max-lines-per-function: ["error", 715]*/
export const TaskListInner = observer(
  (props: {showPromoSettingsBlockHint?: () => void}) => {
    const [state, store] = useTasksListStore()
    const [listsState, listsStore] = useTasksListsStore()
    const [isSettingsVisible, setSettingsVisible] = useState(false)
    const [isSortMenuVisible, setSortMenuVisible] = useState(false)
    const chatChangeListener = useRef<UUID>()
    const settingsButtonRef = useRef<HTMLDivElement>(null)
    const sortButtonRef = useRef<HTMLDivElement>(null)
    const dragCounter = useRef(0)
    const listViewApiRef = useRef<ListStoreExposedApi>(null)
    const [isValidDropTarget, setAsValidDropTarget] = useState(false)
    /**
     * Reference to the promo hint tippy update function
     * It is used to update the hint position when UI above is changed
     * Since the hint update its position only on scroll (or resize) event
     * We need to update it manually when list wrappers are expanded or collapsed
     */
    const promoHintTippyUpdateRef = useRef<(() => void) | null>(null)

    /**
     * Update promo hint tippy on emit ui change event (via triggerUiChange method)
     */
    useEffect(() => {
      return reaction(
        () => listsStore.uiChanged,
        () => {
          promoHintTippyUpdateRef.current?.()
        }
      )
    }, [])

    useSyncEvent('current', {filter: '*', debounceTime: 200}, () => {
      store.syncTasks()
    })

    useSyncEvent(
      'profile',
      {filter: globals.getCompanyId(), debounceTime: 100},
      () => {
        store.syncTasks()
        store.syncTaskList()
      }
    )

    useEffect(() => {
      if (!chatChangeListener.current) {
        chatChangeListener.current = ChatUtils.addChatChangeListener(
          store.setOpenedTaskId
        )
      }

      return () => {
        if (chatChangeListener.current) {
          ChatUtils.removeChatChangeListener(chatChangeListener.current)
          chatChangeListener.current = undefined
        }
      }
    }, [])

    const editListOptions = useMemo(() => {
      return editListMenuOptions().map(menuOption => {
        return {
          ...menuOption,
          onClick: () => store.listMenuOptionClick(menuOption.id)
        }
      })
    }, [])

    const filteredTasks = store.filteredTasks

    const rows = useMemo(
      () => parseTaskToRows(state.listId, filteredTasks, []),
      [filteredTasks]
    )
    const activeColumnsArray = useMemo(
      () => Array.from(state.activeColumns),
      [JSON.stringify(Array.from(state.activeColumns))]
    )

    const keyDown = keyboardHandler({
      enter: store.submitNewListTitle,
      escape: store.stopChangingListName
    })

    useEffect(() => {
      const dragEndHandler = () => {
        // Reset drag enter count when drag ends
        dragCounter.current = 0
      }
      document.addEventListener('dragend', dragEndHandler)

      return () => {
        document.removeEventListener('dragend', dragEndHandler)
      }
    }, [])

    /**
     * This handler captures drag cases when the list is dragged over the list outsides
     * If we're dragging over the list insides it handles by the list-view component
     * But when we're dragging over the list outsides (e.g. over list header)
     * we are talking about moving the task to the list as a whole, without inserting it into a specific order position
     * In here, `setAsValidDropTarget` helps to mark the list as a drop target even if the list itself does not consider itself to be such
     * @param e
     */
    const onDragOverListOutsidesHandler: React.DragEventHandler<
      HTMLDivElement
    > = e => {
      /**
       * If the event is captured by the list itself, we should not handle it here
       * List do not use stopPropagation to make perfect scrollbar works
       */
      if ((e as CapturedByListEvent).capturedByList) {
        if (isValidDropTarget) {
          setAsValidDropTarget(false)
        }
        return
      }

      const draggingData = DragDataClass.get()

      const isDraggingListPrivate =
        draggingData?.type === 'task' &&
        draggingData.listId &&
        listsState.privateTasksLists.includes(draggingData.listId)

      const dropBlocked =
        (draggingData?.type === 'task' &&
          draggingData?.listId === state.listId) ||
        (isDraggingListPrivate && !state.isPrivate) ||
        (state.isPrivate && !isDraggingListPrivate) ||
        state.listId === 'done-tasks'

      DragData.setEffect(e, dropBlocked ? 'none' : 'move')

      if (!dropBlocked) {
        /**
         * Prevent default drag over event to allow drop
         */
        e.preventDefault()
      }

      if (isValidDropTarget === dropBlocked) {
        setAsValidDropTarget(!dropBlocked)
      }
    }

    /**
     * Increase and decrease drag counter to determine if the list is dragged over
     * to show the drop placeholder when the list is empty
     */
    const increaseDragCounter = useCallback(() => {
      dragCounter.current++
    }, [])

    const decreaseDragCounter = useCallback(() => {
      dragCounter.current--
      if (dragCounter.current === 0) {
        setAsValidDropTarget(false)
      }
    }, [])

    /**
     * On drop handler for the list and the outside container
     */
    const commonOnDropHandler = useCallback(() => {
      // Reset drag enter count when drop because final drag leave event is not triggered
      dragCounter.current = 0
      setAsValidDropTarget(false)

      const draggingData = DragDataClass.get()

      if (state.listId === 'done-tasks' || !isValidDropTarget) {
        return
      }

      if (draggingData?.type === 'task') {
        if (state.listId === draggingData.listId) {
          return
        }

        listsStore.onDropItem(
          state.listId,
          draggingData.id,
          undefined,
          draggingData.listId
        )
      }
      listsStore.onDragEnd()
    }, [
      state.listId,
      store.appliedFiltersAmount,
      store.sort,
      isValidDropTarget
    ])

    /**
     * Handle move event for list view specifically
     */
    const onMoveItem = useCallback(
      (
        fromListId: TasksListId,
        toListId: TasksListId,
        itemId: DataObjectId,
        relativeId: DataObjectId,
        before: boolean
      ) => {
        // Reset drag enter count when drop because final drag leave event is not triggered
        dragCounter.current = 0
        setAsValidDropTarget(false)

        if (
          fromListId === toListId &&
          store.sort.type === SortSource.ByCustomOrder
        ) {
          /* Just change filteredTasks order */
          store.changeTasksOrderWithinSameList(itemId, relativeId, before)
          return
        }

        if (store.sort.type !== SortSource.ByCustomOrder) {
          /* Just append task to the end of list */
          commonOnDropHandler()
          return
        }

        /* Move to another list with custom order */
        listsStore.onDropItem(
          state.listId,
          itemId,
          relativeId,
          fromListId,
          before
        )
      },
      []
    )

    /**
     * Create drag data for the task
     */
    const createDragData = useCallback(
      (rowId: DataObjectId | string) => {
        return {
          type: 'task',
          id: rowId,
          isDependent: false,
          listId: state.listId
        } as DragDataObject
      },
      [state.listId]
    )

    const renderItemMenu = useCallback(
      (
        element: HTMLElement,
        row: RowConfig<Hub>,
        parentId: NullableId,
        api: ListStoreExposedApi,
        closeMenu: () => void
      ): React.ReactNode => {
        return (
          <TaskMenu
            element={element}
            closeMenu={closeMenu}
            isSubTask={parentId !== null}
            addSubTask={() => {
              api.addSubTask(row.data.id)
            }}
            rename={() => {
              api.rename(row.data.id)
            }}
          />
        )
      },
      []
    )

    /**
     * Check if lists are compatible for drag and drop between them
     * @param fromListId
     * @param toListId
     */
    const isListsCompatible = (
      fromListId: TasksListId,
      toListId: TasksListId
    ) => {
      return (
        listsState.privateTasksLists.includes(fromListId) ===
        listsState.privateTasksLists.includes(toListId)
      )
    }

    /**
     * Check if the list is done list
     */
    const isDoneList = state.listId === 'done-tasks'

    /**
     * Check if the list is available for drop
     */
    const listAvailableForDrop = useMemo(() => {
      return listsStore.activeDragState
        ? !isDoneList &&
            isListsCompatible(
              listsStore.activeDragState.listId,
              state.listId
            ) &&
            (listsStore.activeDragState.listId !== state.listId ||
              (store.sort.type === SortSource.ByCustomOrder &&
                !store.appliedFiltersAmount))
        : true
    }, [
      listsStore.activeDragState,
      isDoneList,
      state.listId,
      store.appliedFiltersAmount,
      store.sort.type
    ])

    /**
     * This function adds advanced logic for dropping items within the list
     * In here we can let or prevent the drop of the item according to the business logic
     * Unlike `listAvailableForDrop` this function is used for more advanced cases like dropping to/from nested lists
     * @param fromListId
     * @param toListId
     * @param itemId
     * @param relativeId
     * @param toNestingLevel
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    const canDropWithinList = (
      _fromListId: TasksListId,
      _toListId: TasksListId,
      _itemId: DataObjectId,
      _relativeId: DataObjectId,
      toNestingLevel: number
    ) => {
      /* We can not drag to nested list (yet!) */
      if (toNestingLevel > 0) {
        return false
      }
      return listAvailableForDrop
    }

    const handleAddEditorShown = (): void => {
      /**
       * If the editor is shown, we should expand the panel
       */
      store.setPanelExpanded(true)
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    const canDrag = (
      _listId: NullableId,
      _row: Hub,
      _parentId: NullableId,
      nestedLevel: number
    ) => {
      /* Can not drag nested items (yet!) */
      return nestedLevel === 0
    }

    /**
     * Decide if we can create items for the list
     * This function should be memoized since it is involved in rendering
     */
    const canCreateItemsFor = useCallback((parentId: NullableId) => {
      return parentId === null
    }, [])

    /**
     * Check if the list is draggable inside and outside
     */
    const isDraggable =
      state.listId !== 'done-tasks' && !store.appliedFiltersAmount

    /**
     *
     */
    const primaryColumnConfig = useMemo(
      () => ({
        shortName: Lang.msg.fieldTitle,
        fullName: Lang.msg.fieldTitle
      }),
      []
    )

    return (
      <TwTaskListInner
        className={
          isValidDropTarget
            ? 'border-dashed border-action-default border-1 bg-background-hover rounded-8'
            : 'border-1 border-dashed border-transparent'
        }
      >
        <div
          onDragOver={onDragOverListOutsidesHandler}
          onDrop={commonOnDropHandler}
          onDragEnter={increaseDragCounter}
          onDragLeave={decreaseDragCounter}
        >
          <TwListHeader>
            {state.isDeleteListDialogVisible && (
              <Dialog
                close={() => store.setDeleteListVisible(false)}
                submit={store.deleteList}
                width={380}
                title={Lang.msg.confirmation}
                primaryBtnText={Lang.msg.deleteListWithTasks}
                isDelete
              >
                {Lang.msg.deleteListWithTasksConfirmation}
              </Dialog>
            )}
            <TwHeaderLeftArea>
              <TwTitleWrapper
                className={store.isDefaultList ? 'pl-20' : undefined}
              >
                {!store.isDefaultList && (
                  <>
                    <Dropdown
                      hideArrow
                      placement="bottom-start"
                      content={<Menu items={editListOptions} />}
                      visible={state.isEditListMenuVisible}
                      theme="new-menu"
                      onVisibleChange={() =>
                        store.setEditListMenuVisible(false)
                      }
                    >
                      <TwEditListWrapper>
                        <TwIconButton
                          icon="IconCustomDotsVerticalNoPadding"
                          color="secondary"
                          size="small"
                          wrapperSize="big"
                          forcedState={
                            state.isEditListMenuVisible ? 'active' : undefined
                          }
                          onClick={() => store.setEditListMenuVisible(true)}
                          $isTaskColored
                          $isOpen={state.isEditListMenuVisible}
                        />
                      </TwEditListWrapper>
                    </Dropdown>
                  </>
                )}
                <Icon
                  icon={
                    state.expanded
                      ? 'IconCustomChevronUp'
                      : 'IconCustomChevronDown'
                  }
                  onClick={store.togglePanel}
                />
                <TwTitleInnerWrapper onClick={store.togglePanel}>
                  <TwHeaderTitleContainer>
                    {state.isEditingName && (
                      <InputNew
                        isAutoWidth
                        leftIconDisabled
                        cursorView
                        autoFocus
                        size="lg"
                        textInputStyle={inputTitleStyle}
                        inputClassName="!text-20 !h-auto max-w-[350px] !p-0 !pr-4 !font-semibold"
                        value={state.listTitle}
                        onChange={store.onChangeListTitle}
                        onBlur={store.submitNewListTitle}
                        onKeyDown={keyDown}
                        placeholder={Lang.msg.myTasksListName}
                      />
                    )}
                    {!state.isEditingName && (
                      <Tooltip content={state.listTitle} placement="top">
                        <TwHeaderTitleBlock>
                          <TwHeaderTitle>{state.listTitle}</TwHeaderTitle>
                          <TwTaskTitleCountBadge>
                            {store.tasksListAmount}
                          </TwTaskTitleCountBadge>
                        </TwHeaderTitleBlock>
                      </Tooltip>
                    )}
                    {state.isPrivate && (
                      <Tooltip
                        content={Lang.msg.tasksListAvailableOnlyForYouHint}
                        placement="top"
                      >
                        <div>
                          <Tag icon="IconCustomLock" theme="dark">
                            {Lang.msg.tasksListAvailableOnlyForYou}
                          </Tag>
                        </div>
                      </Tooltip>
                    )}
                  </TwHeaderTitleContainer>
                </TwTitleInnerWrapper>
              </TwTitleWrapper>
              <TwHeaderFunctions>
                {/* Columns settings (previously table settings) */}
                {!state.isPrivate && (
                  <>
                    <Tooltip
                      content={
                        <PromoSettings
                          onClose={() => props.showPromoSettingsBlockHint?.()}
                        />
                      }
                      placement="top"
                      visible={!!props.showPromoSettingsBlockHint}
                      className="rounded-12 p-4"
                      interactive
                      offset={[2, 6]}
                      onCreate={instance =>
                        (promoHintTippyUpdateRef.current = () =>
                          instance.popperInstance?.update())
                      }
                      onHide={() => {
                        promoHintTippyUpdateRef.current = null
                      }}
                    >
                      <div>
                        <Dropdown
                          hideArrow
                          placement="bottom"
                          reference={settingsButtonRef.current}
                          content={<ColumnsSettings />}
                          visible={isSettingsVisible}
                          theme="new-menu"
                          onVisibleChange={() => setSettingsVisible(false)}
                        >
                          <TwTableSettingWrapper ref={settingsButtonRef}>
                            <IconButtonNew
                              icon="IconCustomSettings"
                              forcedState={
                                isSettingsVisible ? 'hover' : undefined
                              }
                              onClick={() => setSettingsVisible(true)}
                            />
                          </TwTableSettingWrapper>
                        </Dropdown>
                      </div>
                    </Tooltip>
                    {/* Filters */}
                    <TwHeaderConfigGroup>
                      <IconButtonNew
                        forcedState={
                          state.filtersExpanded || !!store.appliedFiltersAmount
                            ? 'hover'
                            : undefined
                        }
                        icon="IconFilter"
                        onClick={store.toggleFilters}
                      />
                      {!!store.appliedFiltersAmount && (
                        <TwAppliedListTool>
                          {Lang.msg.myTasks.filters} (
                          {store.appliedFiltersAmount})
                        </TwAppliedListTool>
                      )}
                      {!!store.appliedFiltersAmount && (
                        <LinkButtonNew
                          color="notification-primary"
                          onClick={store.resetFilters}
                          className="text-xs-regular"
                        >
                          {Lang.msg.statsTimeline.clearFilters}
                        </LinkButtonNew>
                      )}
                    </TwHeaderConfigGroup>

                    {/* Sort */}
                    <TwHeaderConfigGroup>
                      <SortMenuDropdown
                        columns={store.columns}
                        reference={sortButtonRef.current}
                        isOpened={isSortMenuVisible}
                        closeMenu={() => setSortMenuVisible(false)}
                        onSetNewOrder={(column, asc) => {
                          store.onSortChange({
                            type: SortSource.ByColumn,
                            columnId: column,
                            asc
                          })
                        }}
                      >
                        <TwTableSettingWrapper ref={sortButtonRef}>
                          <IconButtonNew
                            forcedState={
                              isSortMenuVisible ||
                              store.sort.type === SortSource.ByColumn
                                ? 'hover'
                                : undefined
                            }
                            icon="IconCustomSort"
                            onClick={() => setSortMenuVisible(true)}
                          />
                        </TwTableSettingWrapper>
                      </SortMenuDropdown>
                      {store.sort.type === SortSource.ByColumn && (
                        <TwAppliedListTool>
                          {store.getCurrentColumnSortTitle()}
                        </TwAppliedListTool>
                      )}
                      {store.sort.type === SortSource.ByColumn && (
                        <LinkButtonNew
                          color="notification-primary"
                          onClick={store.resetSort}
                          className="text-xs-regular"
                        >
                          {Lang.msg.myTasks.resetSort}
                        </LinkButtonNew>
                      )}
                    </TwHeaderConfigGroup>
                  </>
                )}
                {/* Add task (plus) */}
                {state.listId !== 'done-tasks' && (
                  <IconButtonNew
                    icon="IconCustomPlus"
                    onClick={() => {
                      store.setPanelExpanded(true)
                      listViewApiRef.current?.addSubTask(
                        null,
                        AddMode.BeforeUndoneTasks
                      )
                    }}
                  />
                )}
              </TwHeaderFunctions>
            </TwHeaderLeftArea>
            <TwTableSettingsWrapper>
              {Backend.features.myTasksLists && (
                <>
                  <IconButtonNew
                    disabled={
                      listsState.tasksListsOrder.indexOf(state.listId) === 0
                    }
                    icon="IconArrowUp"
                    onClick={() => listsStore.moveList(state.listId, true)}
                  />
                  <IconButtonNew
                    icon="IconArrowDown"
                    disabled={
                      listsState.tasksListsOrder.indexOf(state.listId) ===
                      listsState.tasksListsOrder.length - 1
                    }
                    onClick={() => listsStore.moveList(state.listId, false)}
                  />
                </>
              )}
            </TwTableSettingsWrapper>
          </TwListHeader>
          {state.filtersExpanded && <TaskListFilters />}
          {state.expanded &&
            Boolean(store.appliedFiltersAmount) &&
            Boolean(state.allUserTasks?.length) &&
            !rows.length && <TwStub>{Lang.msg.tasksNotFoundByFilters}</TwStub>}
          <div className="pl-24 pr-16">
            <ListView
              id={state.listId}
              ref={listViewApiRef}
              columns={store.columns}
              enabledColumnIds={
                state.isPrivate ? EmptyEnabledColumns : activeColumnsArray
              }
              primaryColumnConfig={primaryColumnConfig}
              sort={store.sort}
              order={store.customOrder}
              rows={rows}
              rowRenderer={rowRenderer}
              onItemClick={onRowClick}
              draggable={isDraggable}
              onDragStart={(rowId: DataObjectId | string) =>
                listsStore.onDragStart(state.listId, rowId)
              }
              onDragEnd={listsStore.onDragEnd}
              onChangeOrder={store.setCustomOrder}
              onMoveItem={onMoveItem}
              doneGetter={isTaskDone}
              doneSetter={store.setTaskDone}
              createDragData={createDragData}
              renderItemMenu={renderItemMenu}
              selectName={getTaskTitle}
              selectEditable={(task: Hub) =>
                PermissionsChecker.checkRename(task)
              }
              onNameChange={(taskId: DataObjectId, newName: string) =>
                store.renameTask(taskId, newName)
              }
              allowCreateItems={/*!store.isDefaultList && */ !isDoneList}
              onCreateItem={(
                parentId: DataObjectId | string | null,
                name: string,
                placement: Placement,
                onItemIdReady
              ) => store.addTask(parentId, name, placement, onItemIdReady)}
              canDrag={canDrag}
              canDrop={canDropWithinList}
              canCreateItemsFor={canCreateItemsFor}
              onAddEditorShown={handleAddEditorShown}
              hidden={!state.expanded}
              highlightedItem={listsStore.openedChatId}
              className={cn(
                listAvailableForDrop ? 'opacity-100' : 'opacity-40'
              )}
            />
          </div>
        </div>
      </TwTaskListInner>
    )
  }
)

const ColumnsSettings = observer(() => {
  const [state, store] = useTasksListStore()

  return (
    <TwTableSettingOptions>
      <TwTableSettingsTitle>
        {Lang.msg.availableTableColumns}
      </TwTableSettingsTitle>
      {store.columns
        .filter(taskColumn => !taskColumn.visibility)
        .map(taskColumn => {
          return (
            <TwTableSettingOption
              key={taskColumn.id}
              onClick={() => store.toggleColumnVisibility(taskColumn.id)}
            >
              <CheckboxBase
                value={state.activeColumns.has(taskColumn.id)}
                onClick={event => {
                  event.stopPropagation()
                  store.toggleColumnVisibility(taskColumn.id)
                }}
              />
              <TwTableSettingOptionLabel>
                {taskColumn.fullName}
              </TwTableSettingOptionLabel>
            </TwTableSettingOption>
          )
        })}
    </TwTableSettingOptions>
  )
})
